import axios from "axios";
import { AppState, AppThunk } from "@Store/index";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import config from "@Config/index";
import { getMetaSuccess } from "@App/appReducer";
import { CROSS_PAGE_META } from "../constants/amberPlusForexConstants";
import { fetchUniKitProducts, type CmsProduct } from "@Pages/EssentialKit/services/unikitCms";

export interface LeadBannerInfo {
  firstName: string | null;
  moveInDate: string | null;
  propertyName: string | null;
}

interface AmberPlusCrossPageData {
  readyStatus: "invalid" | "request" | "success" | "failure";
  error: string | null;
  data: {
    partners: any[];
    services: any[];
  };
  leadBannerInfo: LeadBannerInfo | null;
  standardKit: CmsProduct | null;
}

export const initialState: AmberPlusCrossPageData = {
  readyStatus: "invalid",
  error: null,
  data: {
    partners: [],
    services: [],
  },
  leadBannerInfo: null,
  standardKit: null,
};

const amberPlusCrossPageData = createSlice({
  name: "amberPlusCrossPageData",
  initialState,
  reducers: {
    getCrossPageRequesting: (state: AmberPlusCrossPageData) => {
      state.readyStatus = "request";
    },
    getCrossPageSuccess: (state: AmberPlusCrossPageData, { payload }: PayloadAction<any>) => {
      state.readyStatus = "success";
      state.data.partners = payload.data.partners;
      state.data.services = payload.data.services;
    },
    getCrossPageFailure: (state: AmberPlusCrossPageData, { payload }: PayloadAction<any>) => {
      state.readyStatus = "failure";
      state.error = JSON.stringify(payload);
    },
    setLeadBannerInfo: (
      state: AmberPlusCrossPageData,
      { payload }: PayloadAction<LeadBannerInfo | null>,
    ) => {
      state.leadBannerInfo = payload;
    },
    setStandardKit: (
      state: AmberPlusCrossPageData,
      { payload }: PayloadAction<CmsProduct | null>,
    ) => {
      state.standardKit = payload;
    },
  },
});

const shouldFetchPage = (state: AppState) => {
  const status = (state as any).amberPlusCrossPageData?.readyStatus;
  // Re-fetch on initial load and also after a previous failure so that
  // navigating back to this page after a transient error retries automatically.
  return status === "invalid" || status === "failure";
};

const getCmsUrl = (path: string) =>
  __CLIENT__
    ? `${window.location.origin}/proxy/${config.CMS_API_URL}${path}`
    : `${config.CMS_API_URL}${path}`;

const fetchCrossPageData = (): AppThunk => async (dispatch) => {
  dispatch(getCrossPageRequesting());
  try {
    // Same CMS endpoint as the AmberPlus homepage; we only consume `partners` here.
    // Services list stays empty until a real service-tab API is wired up.
    const res = await axios.get(getCmsUrl("/api/amberplus-page/homepage"));
    const partners = (res.data?.partners || []).map((p: any) => ({
      id: String(p.id),
      fieldData: {
        name: p.name ?? "",
        "partner-image": p.logo
          ? { fileId: "", url: p.logo.url ?? "", alt: p.logo.alt ?? null }
          : null,
      },
    }));
    dispatch(
      getCrossPageSuccess({
        data: {
          partners,
          services: [],
        },
      }),
    );
  } catch (err: any) {
    dispatch(getCrossPageFailure(err));
  }
};

export const fetchAmberPlusCrossPageDataIfNeeded = (): AppThunk => (dispatch, getState: any) => {
  if (!shouldFetchPage(getState())) return null;
  return dispatch(fetchCrossPageData());
};

export const setAmberPlusCrossMeta =
  (slug?: string, url?: string | null): AppThunk =>
  (dispatch) => {
    const isKnownService = Boolean(slug && CROSS_PAGE_META[slug]);
    const pageMeta = isKnownService ? CROSS_PAGE_META[slug as string] : CROSS_PAGE_META.overview;
    const canonicalPath = isKnownService ? `/plus-cross/${slug}` : "/plus-cross";
    dispatch(
      getMetaSuccess({
        ...pageMeta,
        url: url || canonicalPath,
        canonical_url_suffix: canonicalPath,
        index: true,
      } as any),
    );
  };

export default amberPlusCrossPageData.reducer;
export const {
  getCrossPageRequesting,
  getCrossPageSuccess,
  getCrossPageFailure,
  setLeadBannerInfo,
  setStandardKit,
} = amberPlusCrossPageData.actions;

/**
 * Fetches lead data by UUID and stores the banner-relevant fields in Redux.
 * Called during SSR loadData when `lead_uuid` is present in the query string.
 * Fails silently — the banner falls back to the generic message if anything goes wrong.
 */
export const fetchLeadBannerData =
  (leadUuid: string): AppThunk =>
  async (dispatch) => {
    try {
      const res = await axios.get(`${config.API_URL}/api/v0/leads/${leadUuid}`);
      const lead = res.data?.data;
      if (!lead) return;
      dispatch(
        setLeadBannerInfo({
          firstName: lead.student_name ? lead.student_name.split(" ")[0] : null,
          moveInDate: lead.move_in_date ?? null,
          propertyName: lead.inventory?.name?.trim() ?? null,
        }),
      );
    } catch {
      // Silently ignore — component falls back to the generic banner message.
    }
  };

/**
 * Fetches the "standard-complete-kit" product from the CMS during SSR and
 * stores it in Redux so EssentialKitHeroContent can read it on first render
 * without a client-side waterfall fetch.
 * Fails silently — the component falls back to FALLBACK_KIT_DATA.
 */
export const fetchStandardKitData = (): AppThunk => async (dispatch) => {
  try {
    const products = await fetchUniKitProducts({
      slug: "standard-complete-kit",
      limit: 1,
      useCache: false,
    });
    dispatch(setStandardKit(products[0] ?? null));
  } catch {
    // Silently ignore — component uses its own fallback.
  }
};
